import { reloginCheck } from '@/authentication/api.ts'
import { goalbookUrls, urlHelpers } from "@/util/links.js";
import { useUserStore } from "@/authentication/user.store.js";
import { RouteLocationNormalized } from "vue-router";
import { useRouterStore } from "@/router/router.store.ts";
import router from "@/router/index.ts";
import { useVisibilityStore } from '@/visibility/visibility.store';
import { ref } from 'vue'

export const showPremiumActionModal = ref(false)

type nextFunction = (to?: string | Partial<RouteLocationNormalized> | false) => void

export function authRouteGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: nextFunction): void | boolean {
  const userStore = useUserStore()

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const adminAccess = to.matched.some(record => record.meta.adminAccess)

  if (!requiresAuth && !adminAccess) {
    next()
    return
  }

  const toUrl = `${urlHelpers.originAndBase}${to.fullPath.slice(1)}`
  const unauthenticatedUrl = urlHelpers.signInReturn(toUrl)

  const checkAndHandleConditions = () => {
    // Conditions met to proceed
    if (userStore.isSuperAdmin || (!adminAccess && userStore.isAuthenticated)) {
      next()
      return
    }

    // Conditions not met - handle based on which criteria wasn't met
    if (!userStore.isAuthenticated) {
      window.location.replace(unauthenticatedUrl)
      return
    }
    if (!userStore.isSuperAdmin) {
      alert('Not authorized')
      if (from.name && !from.meta?.adminAccess) {
        return
      }
      window.location.replace(goalbookUrls.toolkitHome)
    }
  }

  if (userStore.fetching) {
    userStore.pushOnetimeCallback(checkAndHandleConditions)
    return
  }

  checkAndHandleConditions()
}

export async function reloginRouteGuard(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const userStore = useUserStore()
  const expiresAt = userStore.impersonationExpiresAt
  const impersonatingUnexpired =
    userStore.isImpersonating && expiresAt != null && new Date(expiresAt).getTime() > Date.now()
  if (impersonatingUnexpired) {
    return true
  }

  const reloginRoute = to.matched.find(route => route.meta.requiresReloginCheck)
  if (reloginRoute) {
    const reloginCheckDuration = 7
    const returnUrl = `${urlHelpers.originAndBase}${to.fullPath.slice(1)}`
    const shouldForceRelogin = await reloginCheck(reloginCheckDuration, { return_url: returnUrl })

    if (shouldForceRelogin) {
      const routerStore = useRouterStore()
      const reloginSuccessCallback = () => {
        if (to) {
          router.push(to)
        }
      }
      const reloginFailureCallback = () => {
        if (from.name) {
          router.push(from)
        } else {
          window.location.replace(goalbookUrls.toolkitHome)
        }
      }
      routerStore.forceRelogin({
        successCallback: reloginSuccessCallback,
        failureCallback: reloginFailureCallback
      })
      return false
    }
  }
  return true
}

export function betaRouteGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: nextFunction) {
  if (!to.query || !to.query.beta) {
    next()
    return
  }
  const visibilityStore = useVisibilityStore()
  const userStore = useUserStore()


  const checkAndHandleConditions = () => {
    const hasAdminAccess = userStore.isAuthenticated && (
      userStore.isSuperAdmin
      || userStore.isGoalbookBusinessUser
    )
    if (hasAdminAccess) {
      visibilityStore.betaMode = true
    }
    next()
  }

  if (userStore.fetching) {
    userStore.pushOnetimeCallback(checkAndHandleConditions)
    return
  }

  checkAndHandleConditions()
}

export function premiumRouteGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: nextFunction): void {
  const userStore = useUserStore()

  const isPremiumRoute = to.matched.some(record => record.meta.isPremium)

  if (!isPremiumRoute) {
    next()
    return
  }

  const checkAndHandlePremium = () => {
    if (!userStore.isTrial && !userStore.isLicenseExpired) {
      next()
      return
    }
    
    showPremiumActionModal.value = true
    next(false)
  }

  if (userStore.fetching) {
    userStore.pushOnetimeCallback(checkAndHandlePremium)
    return
  }

  checkAndHandlePremium()
}